import type {
  ExpressionPrefixItem as PrefixItem,
  ExpressionAccessorItem as AccessorItem,
  UiEventVocabularyItem as EventItem,
  UiActionVocabularyItem as ActionItem,
} from '@cogo/kg-repository'

export type VocabularyEnumKey = 'prefix' | 'accessor' | 'event' | 'action'

export type VocabularyItem = PrefixItem | AccessorItem | EventItem | ActionItem

export interface VocabularyEnumDescriptor {
  key: VocabularyEnumKey
  label: string
  expectedCount: number
}

/**
 * Static descriptors. expectedCount is the value verified at plan time
 * (loaded shard counts 2026-04-30); actual counts come from SPARQL results
 * and override the badge value.
 */
export const VOCABULARY_ENUMS: readonly VocabularyEnumDescriptor[] = [
  { key: 'prefix', label: 'Prefix', expectedCount: 9 },
  { key: 'accessor', label: 'Accessors', expectedCount: 51 },
  { key: 'event', label: 'Events', expectedCount: 21 },
  { key: 'action', label: 'Actions', expectedCount: 38 },
] as const

/** Re-export item types for downstream consumers (Repository-owned since
 * the kg-client vocabulary bypass migration). */
export type {
  ExpressionPrefixItem as PrefixItem,
  ExpressionAccessorItem as AccessorItem,
  UiEventVocabularyItem as EventItem,
  UiActionVocabularyItem as ActionItem,
} from '@cogo/kg-repository'

/**
 * What the nav can honestly say about one enum's count.
 *
 * Three states, because a badge that renders "not known yet", "known to be
 * zero" and "the query failed" the same way is a badge that cannot be read.
 * The nav used to have one marker ("…") standing in for all three.
 */
export type EnumCount =
  | { kind: 'pending' }
  | { kind: 'ready'; count: number }
  | { kind: 'error' }

/** One `EnumCount` per declared enum — the whole nav's counter row. */
export type EnumCounts = Record<VocabularyEnumKey, EnumCount>

/** Loading state for a single enum's items. */
export type EnumLoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; items: VocabularyItem[] }
  | { kind: 'error'; error: Error }
  | { kind: 'unavailable' }

/**
 * Discriminator helpers — runtime kind detection for table rendering.
 * Each enum's items have a unique property combination (verified against
 * T1 types):
 *   prefix:   semantics + storage + readOnly  → unique by { semantics, storage }
 *   accessor: category + returnKind           → unique by returnKind
 *   event:    domain + (optional) description → unique by domain
 *   action:   category + (optional) description → unique by category-without-returnKind-without-domain
 */
export function isPrefixItem(item: VocabularyItem): item is PrefixItem {
  return 'semantics' in item && 'storage' in item
}

export function isAccessorItem(item: VocabularyItem): item is AccessorItem {
  return 'returnKind' in item
}

export function isEventItem(item: VocabularyItem): item is EventItem {
  return 'domain' in item
}

export function isActionItem(item: VocabularyItem): item is ActionItem {
  return 'category' in item && !('returnKind' in item) && !('domain' in item)
}
